import { MAIN_COLORS, getRarityColors } from '../consts/colors'
import { formatBonusAmount, kopecksToBonusAmount } from './bonusAmount'

const RARITY_NAMES: Record<number, string> = {
  1: 'Обычная',
  2: 'Редкая',
  3: 'Эпическая',
  4: 'Мифическая',
  5: 'Легендарная',
}

export const QR_RESULT_SHARE_WIDTH = 1080
export const QR_RESULT_SHARE_HEIGHT = 1920

export interface QrResultShareBonus {
  label: string
  amount: number
}

export interface QrResultShareStars {
  filled: number
  total: number
}

export interface QrResultShareModel {
  kind: 'toy' | 'achievement'
  name: string
  imageDataUrl: string
  rarity: number
  title: string
  subtitle: string
  detail?: string
  progressText?: string
  bonuses: QrResultShareBonus[]
  stars?: QrResultShareStars
}

export interface QrResultShareModelInput {
  kind: 'toy' | 'achievement'
  name: string
  imageDataUrl: string
  rarity?: number
  level?: number | null
  maxLevel?: number
  isMilestone?: boolean
  isMaxLevel?: boolean
  bonusAdded?: number
  milestoneBonusPoints?: number
  nextMilestoneLevel?: number | null
  nextBonusLevel?: number | null
  progress?: number
  threshold?: number
  isLutyan?: boolean
}

export type ShareQrResultOutcome = 'shared' | 'unsupported'

export interface ShareQrResultOptions {
  fileName?: string
  title?: string
  text?: string
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

/**
 * Turns API result fields into a stable, presentation-only model. Keeping this
 * mapping outside React also prevents bonus labels and star rules from drifting
 * between the result screen and its PNG representation.
 */
export const createQrResultShareModel = (
  input: QrResultShareModelInput,
): QrResultShareModel => {
  const bonusAdded = Math.max(0, input.bonusAdded || 0)
  const rarity = clamp(Math.round(input.rarity || 1), 1, 5)

  if (input.kind === 'achievement') {
    const bonuses: QrResultShareBonus[] = []
    if (bonusAdded > 0) {
      bonuses.push({ label: 'Награда за достижение', amount: bonusAdded })
    }
    if ((input.milestoneBonusPoints || 0) > 0) {
      bonuses.push({
        label: 'Бонус за достижение',
        amount: input.milestoneBonusPoints || 0,
      })
    }

    let progressText: string | undefined
    if ((input.threshold || 0) > 0) {
      const progress = Math.max(0, input.progress || 0)
      const threshold = input.threshold || 0
      progressText = progress >= threshold
        ? 'Выполнено!'
        : `${progress} из ${threshold}`
    }

    return {
      kind: input.kind,
      name: input.name,
      imageDataUrl: input.imageDataUrl,
      rarity,
      title: 'Поздравляем',
      subtitle: input.isMilestone ? 'бонус за достижение' : 'открыто достижение',
      progressText,
      bonuses,
    }
  }

  const maxLevel = Math.max(1, input.maxLevel || 5)
  const level = clamp(Math.round(input.level || 1), 1, maxLevel)
  const isMaxLevel = Boolean(input.isMaxLevel || level >= maxLevel)
  const nextBonusLevel = input.nextBonusLevel ?? input.nextMilestoneLevel
  const displayedBonusAdded = kopecksToBonusAmount(input.bonusAdded)
  const bonuses: QrResultShareBonus[] = []

  bonuses.push({
    label: isMaxLevel
      ? 'Максимальный уровень'
      : `Уровень ${level}`,
    amount: displayedBonusAdded,
  })

  let detail: string | undefined
  if (isMaxLevel) {
    detail = 'Эта фигурка прокачана на максимум!'
  } else if (nextBonusLevel && nextBonusLevel > level) {
    detail = `Следующий бонус на уровне ${nextBonusLevel}`
  } else {
    detail = input.isLutyan
      ? 'Бонусный цифровой дроп к твоей капсуле'
      : 'Этот лутбол добавлен в вашу коллекцию'
  }

  return {
    kind: input.kind,
    name: input.name,
    imageDataUrl: input.imageDataUrl,
    rarity,
    title: `${RARITY_NAMES[rarity]}${rarity === 5 ? '!' : ''}`,
    subtitle: 'LootBall в вашей коллекции',
    detail,
    bonuses,
    stars: { filled: level, total: maxLevel },
  }
}

const loadCanvasImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Не удалось загрузить изображение для PNG'))
    image.src = src
  })

const roundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  const r = Math.min(radius, width / 2, height / 2)
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + width - r, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + r)
  ctx.lineTo(x + width, y + height - r)
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
  ctx.lineTo(x + r, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

const drawContainedImage = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
) => {
  const imageWidth = image.naturalWidth || image.width
  const imageHeight = image.naturalHeight || image.height
  if (!imageWidth || !imageHeight) return
  const scale = Math.min(width / imageWidth, height / imageHeight)
  const targetWidth = imageWidth * scale
  const targetHeight = imageHeight * scale
  ctx.drawImage(
    image,
    x + (width - targetWidth) / 2,
    y + (height - targetHeight) / 2,
    targetWidth,
    targetHeight,
  )
}

const splitText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
) => {
  const words = text.trim().split(/\s+/)
  const lines: string[] = []
  let current = ''

  words.forEach(word => {
    const candidate = current ? `${current} ${word}` : word
    if (ctx.measureText(candidate).width <= maxWidth || !current) {
      current = candidate
    } else {
      lines.push(current)
      current = word
    }
  })
  if (current) lines.push(current)

  if (lines.length <= maxLines) return lines
  const visible = lines.slice(0, maxLines)
  let last = visible[maxLines - 1]
  while (last && ctx.measureText(`${last}…`).width > maxWidth) {
    last = last.slice(0, -1)
  }
  visible[maxLines - 1] = `${last}…`
  return visible
}

const drawCenteredLines = (
  ctx: CanvasRenderingContext2D,
  text: string,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines = 2,
) => {
  splitText(ctx, text, maxWidth, maxLines).forEach((line, index) => {
    ctx.fillText(line, QR_RESULT_SHARE_WIDTH / 2, y + index * lineHeight)
  })
}

// A small bitmap produces crisp stars independently of the installed font.
const STAR_BITMAP = [
  '....##....',
  '....##....',
  '...####...',
  '##########',
  '.########.',
  '..######..',
  '..######..',
  '.###..###.',
  '##......##',
]

const drawPixelStar = (
  ctx: CanvasRenderingContext2D,
  centerX: number,
  y: number,
  color: string,
  filled: boolean,
) => {
  const pixel = 10
  const width = STAR_BITMAP[0].length * pixel
  ctx.save()
  ctx.fillStyle = color
  ctx.globalAlpha = filled ? 1 : 0.22
  if (filled) {
    ctx.shadowColor = color
    ctx.shadowBlur = 18
  }
  STAR_BITMAP.forEach((row, rowIndex) => {
    row.split('').forEach((cell, columnIndex) => {
      if (cell === '#') {
        ctx.fillRect(centerX - width / 2 + columnIndex * pixel, y + rowIndex * pixel, pixel, pixel)
      }
    })
  })
  ctx.restore()
}

const drawBonus = (
  ctx: CanvasRenderingContext2D,
  bonus: QrResultShareBonus,
  y: number,
  primary: string,
  secondary: string,
) => {
  roundedRect(ctx, 145, y, 790, 158, 28)
  const background = ctx.createLinearGradient(145, y, 935, y + 158)
  background.addColorStop(0, MAIN_COLORS.purple_100)
  background.addColorStop(1, MAIN_COLORS.purple_200)
  ctx.fillStyle = background
  ctx.fill()
  ctx.strokeStyle = primary
  ctx.globalAlpha = 0.65
  ctx.lineWidth = 3
  ctx.stroke()
  ctx.globalAlpha = 1

  ctx.textAlign = 'center'
  ctx.fillStyle = MAIN_COLORS.purple_900
  ctx.font = '32px ComplianceSans, Arial, sans-serif'
  ctx.fillText(bonus.label, QR_RESULT_SHARE_WIDTH / 2, y + 51)

  const amountGradient = ctx.createLinearGradient(350, y, 730, y)
  amountGradient.addColorStop(0, primary)
  amountGradient.addColorStop(1, secondary)
  ctx.fillStyle = amountGradient
  ctx.font = '700 56px ComplianceSans, Arial, sans-serif'
  ctx.fillText(`+ ${formatBonusAmount(bonus.amount)} Бонусов`, QR_RESULT_SHARE_WIDTH / 2, y + 121)
}

const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => {
    try {
      canvas.toBlob(blob => {
        if (blob) resolve(blob)
        else reject(new Error('Браузер не смог создать PNG'))
      }, 'image/png')
    } catch (error) {
      reject(error)
    }
  })

/** Creates the static final-screen artwork that can be shared as a PNG file. */
export const createQrResultSharePng = async (
  model: QrResultShareModel,
): Promise<Blob> => {
  if (!model.imageDataUrl) throw new Error('Изображение результата не загружено')

  const canvas = document.createElement('canvas')
  canvas.width = QR_RESULT_SHARE_WIDTH
  canvas.height = QR_RESULT_SHARE_HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas не поддерживается этим браузером')

  if (document.fonts) {
    try {
      await Promise.all([
        document.fonts.load('32px ComplianceSans'),
        document.fonts.load('700 64px ComplianceSans'),
      ])
    } catch {
      // Canvas has explicit system fallbacks, so a failed custom font is safe.
    }
  }
  const image = await loadCanvasImage(model.imageDataUrl)
  const rarityColors = getRarityColors(model.rarity)

  const pageGradient = ctx.createLinearGradient(0, 0, 0, QR_RESULT_SHARE_HEIGHT)
  pageGradient.addColorStop(0, MAIN_COLORS.purple_100)
  pageGradient.addColorStop(0.48, MAIN_COLORS.purple_200)
  pageGradient.addColorStop(1, MAIN_COLORS.purple_100)
  ctx.fillStyle = pageGradient
  ctx.fillRect(0, 0, QR_RESULT_SHARE_WIDTH, QR_RESULT_SHARE_HEIGHT)

  const aura = ctx.createRadialGradient(540, 850, 40, 540, 850, 600)
  aura.addColorStop(0, `${rarityColors.primary}88`)
  aura.addColorStop(0.6, `${rarityColors.secondary}28`)
  aura.addColorStop(1, `${rarityColors.secondary}00`)
  ctx.fillStyle = aura
  ctx.fillRect(0, 230, QR_RESULT_SHARE_WIDTH, 1240)

  roundedRect(ctx, 48, 54, 984, 1812, 48)
  const cardGradient = ctx.createLinearGradient(0, 54, 0, 1866)
  cardGradient.addColorStop(0, MAIN_COLORS.purple_100)
  cardGradient.addColorStop(1, MAIN_COLORS.purple_300)
  ctx.fillStyle = cardGradient
  ctx.fill()
  ctx.strokeStyle = `${rarityColors.primary}99`
  ctx.lineWidth = 4
  ctx.stroke()

  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'
  const titleGradient = ctx.createLinearGradient(260, 0, 820, 0)
  titleGradient.addColorStop(0, MAIN_COLORS.yellow)
  titleGradient.addColorStop(1, MAIN_COLORS.pink)
  ctx.fillStyle = titleGradient
  ctx.font = '700 72px ComplianceSans, Arial, sans-serif'
  drawCenteredLines(ctx, model.title, 176, 850, 80, 2)

  ctx.fillStyle = MAIN_COLORS.yellow
  ctx.font = '700 40px ComplianceSans, Arial, sans-serif'
  drawCenteredLines(ctx, model.subtitle, 274, 850, 48, 2)

  roundedRect(ctx, 122, 350, 836, 720, 44)
  const imagePanel = ctx.createLinearGradient(122, 350, 958, 1070)
  imagePanel.addColorStop(0, MAIN_COLORS.purple_100)
  imagePanel.addColorStop(1, MAIN_COLORS.purple_200)
  ctx.fillStyle = imagePanel
  ctx.fill()
  ctx.strokeStyle = `${rarityColors.primary}55`
  ctx.lineWidth = 3
  ctx.stroke()
  drawContainedImage(ctx, image, 152, 380, 776, 660)

  ctx.fillStyle = MAIN_COLORS.yellow
  ctx.font = '700 64px ComplianceSans, Arial, sans-serif'
  drawCenteredLines(ctx, model.name, 1164, 850, 70, 2)

  if (model.detail) {
    ctx.fillStyle = MAIN_COLORS.purple_900
    ctx.font = '34px ComplianceSans, Arial, sans-serif'
    drawCenteredLines(ctx, model.detail, 1309, 780, 42, 2)
  }

  const visibleBonuses = model.bonuses.slice(0, 2)
  const bonusStartY = visibleBonuses.length > 1 ? 1380 : 1430
  visibleBonuses.forEach((bonus, index) => {
    drawBonus(ctx, bonus, bonusStartY + index * 174, rarityColors.primary, rarityColors.secondary)
  })

  if (model.stars) {
    const total = Math.max(1, model.stars.total)
    const filled = clamp(model.stars.filled, 0, total)
    const gap = Math.min(158, 760 / total)
    const firstX = QR_RESULT_SHARE_WIDTH / 2 - ((total - 1) * gap) / 2
    const starY = visibleBonuses.length > 1 ? 1740 : 1690
    for (let index = 0; index < total; index += 1) {
      drawPixelStar(
        ctx,
        firstX + index * gap,
        starY,
        rarityColors.primary,
        index < filled,
      )
    }
  } else if (model.progressText) {
    ctx.fillStyle = MAIN_COLORS.purple_900
    ctx.font = '700 36px ComplianceSans, Arial, sans-serif'
    ctx.fillText(model.progressText, QR_RESULT_SHARE_WIDTH / 2, 1780)
  }

  ctx.fillStyle = '#FFFFFF'
  ctx.globalAlpha = 0.58
  ctx.font = '28px ComplianceSans, Arial, sans-serif'
  ctx.fillText('LOOTBALL', QR_RESULT_SHARE_WIDTH / 2, 1830)
  ctx.globalAlpha = 1

  return canvasToBlob(canvas)
}

export const createQrResultShareFile = (
  png: Blob,
  fileName = 'lootball-result.png',
) => new File([png], fileName, { type: 'image/png', lastModified: Date.now() })

/**
 * Opens the platform file share sheet when it is available. Unsupported is a
 * normal outcome so the caller can display its own download/preview fallback.
 * Share errors (including AbortError) intentionally remain observable.
 */
export const shareQrResultPng = async (
  png: Blob,
  options: ShareQrResultOptions = {},
): Promise<ShareQrResultOutcome> => {
  const file = createQrResultShareFile(png, options.fileName)
  const files = [file]
  if (!navigator.share || !navigator.canShare?.({ files })) return 'unsupported'

  await navigator.share({
    files,
    title: options.title || 'Мой LootBall',
    text: options.text,
  })
  return 'shared'
}
